<template>
  <b-container class="gm-admin internal-persons">
    <b-btn class="float-right" variant="primary" @click="back">
      Back
    </b-btn>

    <h2>Internal Persons</h2>

    <v-wait :for="loaderId">
      <HalfCircleSpinner slot="waiting" />
      <template slot="waiting">
        <div class="spinner-message my-3 text-center">
          Loading the internal persons...
        </div>
      </template>
      <DataTable
        ref="dataTable"
        :show-filters="true"
        :show-pager="true"
        :items="filteredInternalPersons"
        :fields="fields"
        :filters="filters"
        :filter-title="'Filter Internal Persons'"
        :no-local-sorting="false"
      >
        <!-- template for filters -->
        <template slot="filters">
          <b-row align-h="between">
            <b-col sm="6">
              <b-btn variant="primary" @click="createInternalPerson">
                Add New
              </b-btn>
            </b-col>
            <b-col sm="6" align-self="center" class="text-right">
              <b-form-checkbox
                v-model="filters.disabled.value"
                value="1"
                unchecked-value="0"
              >
                Show Disabled
              </b-form-checkbox>
            </b-col>
          </b-row>
        </template>
        <!-- templates for custom columns -->
        <template slot="actions" slot-scope="data">
          <b-dropdown
            split
            variant="info"
            size="sm"
            text="Edit"
            @click.stop="editInternalPerson(data.item.id)"
          >
            <b-dropdown-item-button
              v-if="data.item.disabled === 0"
              :id="'disabled_' + data.item.id"
              @click.stop="disableInternalPerson(data.item.id)"
              class="btn-sm"
            >
              Disable
              <template v-if="!!data.item.societies.length">
                <b-tooltip
                  ref="tooltip"
                  :target="'disabled_' + data.item.id"
                  triggers="hover"
                >
                  Societies:
                  <div
                    v-for="society in data.item.societies"
                    :key="`${data.item.id}_${society.id}`"
                  >
                    {{ society.name }}
                  </div>
                </b-tooltip>
              </template>
            </b-dropdown-item-button>
          </b-dropdown>
        </template>
      </DataTable>
    </v-wait>

    <modal-edit-form
      v-if="showModal"
      :initial-person="editedPerson"
      ref="modal"
      @modalCancel="onModalCancel"
      @personSaved="onPersonSaved"
    ></modal-edit-form>
  </b-container>
</template>

<script>
import Api from '@/providers/Api.js';
import DataTable from '@/components/DataTable.vue';
import { HalfCircleSpinner } from 'epic-spinners';
import ModalEditForm from '@/components/gm_admin/internal_person/ModalEditForm';

export default {
  name: 'List',
  components: {
    DataTable,
    HalfCircleSpinner,
    ModalEditForm,
  },
  data() {
    return {
      persons: [],
      editedPerson: null,
      showModal: false,
      filters: {
        disabled: { field: 'disabled', type: 'boolean', value: '0' },
      },
      fields: [
        { key: 'first_name', label: 'First', sortable: true },
        { key: 'last_name', label: 'Last', sortable: true },
        { key: 'phone_num', label: 'Phone', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'actions', label: '', custom: true },
      ],
    };
  },
  created: function () {
    this.refreshData();
  },
  computed: {
    loaderId: function () {
      return `loading_${this.$options.name}`;
    },
    filteredInternalPersons: function () {
      const filterByDisabled =
        this.filters.disabled && this.filters.disabled.value !== '0';
      if (!filterByDisabled) {
        return this.persons.filter((m) => {
          return parseInt(m.disabled) === parseInt(this.filters.disabled.value);
        });
      }
      return this.persons;
    },
  },
  methods: {
    back() {
      this.$router.go(-1);
    },
    refreshData: async function () {
      this.$wait.start(this.loaderId);
      try {
        const persons = await Api.getInternalPersons();
        persons.forEach((p) => {
          p._rowVariant = p.disabled ? 'secondary' : '';
        });
        this.persons = persons;
      } catch (error) {
        this.$toasted.error('Upload data error', { duration: 3000 });
      }
      this.$wait.end(this.loaderId);
    },
    createInternalPerson() {
      this.openEditModal();
    },
    editInternalPerson(id) {
      const person = this.persons.find((p) => p.id === id);
      this.openEditModal(person);
    },
    openEditModal(person = null) {
      this.editedPerson = person;
      this.showModal = true;
    },
    disableInternalPerson: async function (id) {
      const person = this.persons.find((p) => p.id === id);

      if (person.societies.length) {
        const societies = [];
        person.societies.forEach((s) => societies.push(s.name));
        this.$toasted.error(
          `The Person is associated with Societies:<br>${societies.join(
            '<br>'
          )}`,
          { duration: 3000 }
        );
        return false;
      }

      try {
        await Api.disableInternalPerson(person.id);
        person.disabled = 1;
        person._rowVariant = 'secondary';
        this.$toasted.success('Person successfully disabled.', {
          duration: 3000,
        });
      } catch (e) {
        this.$toasted.error('Person Updating error', { duration: 3000 });
      }
    },
    onModalCancel() {
      this.editedPerson = null;
      this.showModal = false;
    },
    onPersonSaved(person) {
      const index = this.persons.findIndex((p) => p.id === person.id);
      if (index !== -1) {
        this.$set(this.persons, index, person);
      } else {
        this.$set(this.persons, this.persons.length, person);
      }
    },
  },
};
</script>

<style scoped></style>
